@if (client?.civilId) {
<div class="top-padding-16">
  <div layout="row"
       layout-align="space-between center">
    <h3 class="edit-screen-card-title">
      {{ 'bankInfo.title' | translate }}
    </h3>
    <div layout="row"
         layout-align="end center">
      <button mat-icon-button
              [disabled]="isLoading"
              (click)="refreshReport()"
              matTooltipPosition="above"
              matTooltip="{{ 'bankInfo.refresh' | translate }}">
        <mat-icon>refresh</mat-icon>
      </button>
    </div>
  </div>

  @if (hasData) {
  <!-- ── Filters ────────────────────────────────────────────── -->
  <div layout="row"
       layout-align="space-between center"
       class="filters-row">
    <mat-form-field flex
                    subscriptSizing="dynamic">
      <mat-label>{{ 'bankInfo.filters.dateFrom' | translate }}</mat-label>
      <input matInput
             [matDatepicker]="dateFromPicker"
             [(ngModel)]="dateFromFilter"
             (ngModelChange)="onFilterChange()" />
      <mat-datepicker-toggle matSuffix
                             [for]="dateFromPicker"></mat-datepicker-toggle>
      <mat-datepicker [touchUi]="false"
                      #dateFromPicker></mat-datepicker>
    </mat-form-field>

    <mat-form-field flex
                    subscriptSizing="dynamic">
      <mat-label>{{ 'bankInfo.filters.dateTo' | translate }}</mat-label>
      <input matInput
             [matDatepicker]="dateToPicker"
             [(ngModel)]="dateToFilter"
             (ngModelChange)="onFilterChange()" />
      <mat-datepicker-toggle matSuffix
                             [for]="dateToPicker"></mat-datepicker-toggle>
      <mat-datepicker [touchUi]="false"
                      #dateToPicker></mat-datepicker>
    </mat-form-field>

    <mat-form-field flex>
      <mat-label>{{ 'bankInfo.filters.amountFrom' | translate }}</mat-label>
      <input matInput
             type="number"
             [(ngModel)]="amountFromFilter"
             (ngModelChange)="onFilterChange()" />
    </mat-form-field>

    <mat-form-field flex>
      <mat-label>{{ 'bankInfo.filters.amountTo' | translate }}</mat-label>
      <input matInput
             type="number"
             [(ngModel)]="amountToFilter"
             (ngModelChange)="onFilterChange()" />
    </mat-form-field>

    <mat-form-field flex="20">
      <mat-label>{{
        'bankInfo.filters.description' | translate
        }}</mat-label>
      <input matInput
             type="text"
             [(ngModel)]="descriptionFilter"
             (ngModelChange)="onFilterChange()" />
    </mat-form-field>

    <button mat-icon-button
            (click)="clearFilters()"
            matTooltip="{{ 'bankInfo.filters.clear' | translate }}">
      <mat-icon>clear</mat-icon>
    </button>
  </div>
  }

  @if (isLoading) {
  <div class="progress-spinner-container">
    <mat-spinner class="progress-spinner"
                 strokeWidth="10"
                 [diameter]="150"></mat-spinner>
  </div>
  }

  @if (!isLoading && !hasData) {
  <div flex
       layout-align="center center"
       class="text-center">
    <h4 [translate]="'bankInfo.noData'"></h4>
  </div>
  }

  @if (!isLoading && hasData) {
  @for (view of bankAccountViews; track view.account.iban; let i = $index) {
  <mat-card appearance="outlined"
            class="bank-account-card">
    <mat-card-content class="remove-mat-form-field-wrapper">
      <!-- Header -->
      <div layout="row"
           layout-align="space-between start">
        <!-- Left: Logo + Account info -->
        <div layout="row"
             layout-align="start start"
             class="account-left">
          <img class="bank-logo"
               [src]="view.account.iban | ibanIcon"
               [matTooltip]="view.account.iban | formatIban: 'bankName'"
               [alt]="'Bank logo for ' + (view.account.iban | formatIban)"
               (error)="
                    $event.target.src = 'assets/images/bank-icons/generic-bank-icon.png'
                  " />
          <div layout="column"
               class="account-info">
            <div layout="row"
                 layout-align="start center"
                 class="iban-row">
              <span class="iban-text">{{ view.account.iban }}</span>
              <span>-</span>
              <span class="balance-text">
                @if (view.balance !== null) {
                {{ view.balance }} {{ view.balanceCurrency }}
                } @else {
                -
                }
              </span>
              @if (view.balanceReferenceDate !== null) {
              {{'global.towards' | translate}} {{ view.balanceReferenceDate | itfgDateFormat: dateFormat }}
              }
            </div>
            <span class="account-type">{{ view.account.product }}</span>
            <span class="account-owner">{{
              view.account.ownerName
              }}</span>
          </div>
        </div>

        <!-- Right: Brand warning + Consent chip + menu + expand -->
        <div layout="row"
             layout-align="end start">
          <div layout="column"
               layout-align="start end"
               class="gap-8px">
            <div layout="row"
                 layout-align="start center"
                 class="gap-8px">
              @if (view.isDifferentBrand) {
              <mat-icon class="brand-warning"
                        color="warn"
                        matTooltip="{{ 'bankInfo.differentBrand' | translate: { brand: view.brand } }}"
                        matTooltipPosition="above">
                warning
              </mat-icon>
              }

              <mat-chip [highlighted]="true"
                        [ngClass]="{
                          'consent-active': view.isConsentActive,
                          'consent-inactive': !view.isConsentActive,
                          'clickable': true,
                        }"
                        (click)="openPermissionsDialog(view)">
                {{ view.consentStatusLabel | translate }}
              </mat-chip>

              <button mat-icon-button
                      [matMenuTriggerFor]="consentMenu"
                      class="consent-menu-trigger">
                <mat-icon>more_vert</mat-icon>
              </button>

              <mat-menu #consentMenu="matMenu">
                <button mat-menu-item
                        (click)="openPermissionsDialog(view)">
                  <mat-icon>shield</mat-icon>
                  <span>{{ 'bankInfo.consent.viewPermissions' | translate }}</span>
                </button>
                @if (view.isConsentActive) {
                <button mat-menu-item
                        (click)="deactivateConsent(view)">
                  <mat-icon color="warn">link_off</mat-icon>
                  <span>{{ 'bankInfo.consent.deactivate' | translate }}</span>
                </button>
                }
              </mat-menu>
            </div>
            @if (view.consent) {
            <span class="consent-detail gap-8px">
              <span matTooltip="{{ 'bankInfo.consent.createdAt' | translate }}"
                    matTooltipPosition="above">
                {{ view.consent.created_at | date: 'dd.MM.yyyy' }}
              </span>
              -
              <span matTooltip="{{ 'bankInfo.consent.validUntil' | translate }}"
                    matTooltipPosition="above">
                {{ view.consent.valid_until | date: 'dd.MM.yyyy' }}
              </span>
            </span>
            }
          </div>

          <button mat-icon-button
                  (click)="toggleExpand(view)"
                  matTooltip="{{ (view.expanded ? 'bankInfo.hideTransactions' : 'bankInfo.showTransactions') | translate }}">
            <mat-icon>{{ view.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </div>
      </div>

      <!-- Turnover -->
      <div layout="row"
           layout-align="start center"
           class="turnover-row">
        <span>{{ 'bankInfo.turnover31Days' | translate }}:&nbsp;</span>
        <span class="turnover-incoming">+{{ view.totalIncoming31Days | number:'1.2-2':'bg' }}
          {{ view.balanceCurrency }}</span>
        <span>&nbsp;/&nbsp;</span>
        <span class="turnover-outgoing">-{{ view.totalOutgoing31Days | number:'1.2-2':'bg' }}
          {{ view.balanceCurrency }}</span>
      </div>

      <!-- Transaction table + Paginator -->
      @if (view.expanded) {
      @if (view.transactions.length > 0) {
      <itfg-data-table [data]="view.pagedTransactions"
                       [columns]="transactionColumns"
                       [isClickable]="false">
        <ng-template itfgDataTableTemplate="bookingDate"
                     let-value="value">
          {{ !!value ? (value | date: 'dd.MM.yyyy') : '-' }}
        </ng-template>
        <ng-template itfgDataTableTemplate="direction"
                     let-row="row">
          <itfg-bank-transaction-direction-icon [creditDebitIndicator]="row.creditDebitIndicator">
          </itfg-bank-transaction-direction-icon>
        </ng-template>
        <ng-template itfgDataTableTemplate="counterpartyName"
                     let-row="row">
          {{ row.counterpartyName }}
        </ng-template>
        <ng-template itfgDataTableTemplate="counterpartyIban"
                     let-row="row">
          {{ row.counterpartyIban }}
        </ng-template>
        <ng-template itfgDataTableTemplate="amount"
                     let-row="row">
          <span [class]="
                        row.creditDebitIndicator ===
                        creditDebitIndicators.CREDIT
                          ? 'amount-credit'
                          : 'amount-debit'
                      ">
            {{
            row.creditDebitIndicator ===
            creditDebitIndicators.CREDIT
            ? '+'
            : ''
            }}{{ row.transactionAmount.amount }}
            {{ row.transactionAmount.currency }}
          </span>
        </ng-template>
        <ng-template itfgDataTableTemplate="description"
                     let-row="row">
          {{ row.remittanceInformationUnstructured || '-' }}
        </ng-template>
      </itfg-data-table>

      <mat-paginator [pageIndex]="view.currentPage"
                     [length]="view.transactions.length"
                     [pageSize]="view.pageSize"
                     [pageSizeOptions]="[10, 20, 50]"
                     (page)="pageChanged(view, $event)"
                     showFirstLastButtons>
      </mat-paginator>
      } @else {
      <div layout="row"
           layout-align="center"
           class="no-transactions">
        <mat-icon>search_off</mat-icon>
        <span>{{ 'bankInfo.noTransactions' | translate }}</span>
      </div>
      }
      }
    </mat-card-content>
  </mat-card>
  }
  }
</div>
}
